(function () {
  'use strict';

  angular.module('ringbaPortal.services').factory('accountsService', accountsService);

  function accountsService(
    _,
    $http,
    $q,
    $log,
    cacheService,
    authService,
    userSettingsService,
    ngAuthSettings,
    promiseCacheService,
    toaster,
    userGroupService,
    featureFlagsService,
    systemFlagsService,
    VuexStore,
    VuexMutationTypes,
    whitelabelService
  ) {
    'ngInject';

    const serviceBase = ngAuthSettings.apiServiceBaseUri;
    const accountsServiceFactory = {
      currentAccounts: null,
      currentUserName: null,
      currentAccountId: null,
      currentAccount: null,
      currentAccountUser: null,
      currentPermissions: null,
      accountPermissions: null,
      currentAccountIsPaused: null,
    };

    let currentAccountId = '';
    let currentUserName = authService.authentication.userName;
    let currentAccount = undefined;
    let currentPermissions = undefined;
    let currentAccountUser = undefined;
    let currentAccountUserGroups = [];
    let currentAccountPermissions = undefined;
    let currentAccountIsPaused = false;
    const promiseAccountCacheKey = 'accountService-promise';
    const promiseAccountsCacheKey = 'accountService-accounts-promise';


    accountsServiceFactory.resetAccountData = resetAccountData;
    accountsServiceFactory.selectAccount = selectAccount;
    accountsServiceFactory.getAccount = getAccount;
    accountsServiceFactory.getAccountRoles = getAccountRoles;
    accountsServiceFactory.getAccountDefaultPermissionItem = getAccountDefaultPermissionItem;
    accountsServiceFactory.updateAccount = updateAccount;
    accountsServiceFactory.createAccount = createAccount;
    accountsServiceFactory.saveDefaultAccount = saveDefaultAccount;
    accountsServiceFactory.loadAccountsData = loadAccountsData;
    accountsServiceFactory.setCurrentAccount = setCurrentAccount;
    accountsServiceFactory.hasAccess = hasAccess;
    accountsServiceFactory.isAffiliate = isAffiliate;
    accountsServiceFactory.isBuyer = isBuyer;
    accountsServiceFactory.isAffiliateOnly = isAffiliateOnly;
    accountsServiceFactory.isBuyerOnly = isBuyerOnly;
    accountsServiceFactory.isAffiliateManager = isAffiliateManager;
    accountsServiceFactory.isBuyerManager = isBuyerManager;
    accountsServiceFactory.isAdmin = isAdmin;
    accountsServiceFactory.isReportReadOnlyUser = isReportReadOnlyUser;
    accountsServiceFactory.isReadOnlySuperUser = isReadOnlySuperUser;

    // This will automatically commit a mutation when currentAccountId is set on this service
    Object.defineProperty(accountsServiceFactory, 'currentAccountId', {
      get: function () {
        return currentAccountId;
      },
      set: function (newValue) {
        currentAccountId = newValue;
        VuexStore.commit(VuexMutationTypes.SET_CURRENT_ACCOUNT_ID, newValue);
        _syncIdentity();
      }
    });

    Object.defineProperty(accountsServiceFactory, 'currentUserName', {
      get: function () {
        return currentUserName;
      },
      set: function (newValue) {
        currentUserName = newValue;
        _syncIdentity();
      }
    });

    Object.defineProperty(accountsServiceFactory, 'currentAccount', {
      get: function () {
        return currentAccount;
      },
      set: function (newValue) {
        currentAccount = newValue;
        VuexStore.commit(VuexMutationTypes.SET_CURRENT_ACCOUNT, newValue);
      }
    });

    Object.defineProperty(accountsServiceFactory, 'currentPermissions', {
      get: function () {
        return currentPermissions;
      },
      set: function (newValue) {
        currentPermissions = newValue;
        VuexStore.commit(VuexMutationTypes.SET_CURRENT_ACCOUNT_PERMISSIONS, newValue);
      }
    });

    Object.defineProperty(accountsServiceFactory, 'currentAccountUser', {
      get: function () {
        return currentAccountUser;
      },
      set: function (newValue) {
        currentAccountUser = newValue;
        VuexStore.commit(VuexMutationTypes.SET_CURRENT_ACCOUNT_USER, newValue);
      }
    });

    Object.defineProperty(accountsServiceFactory, 'accountPermissions', {
      get: function () {
        return currentAccountPermissions;
      },
      set: function (newValue) {
        currentAccountPermissions = newValue;
        VuexStore.commit(VuexMutationTypes.SET_CURRENT_ACCOUNT_ITEM_PERMISSIONS, newValue);
      }
    });

    Object.defineProperty(accountsServiceFactory, 'currentAccountUserGroups', {
      get: function () {
        return currentAccountUserGroups;
      },
      set: function (newValue) {
        currentAccountUserGroups = newValue;
        VuexStore.commit(VuexMutationTypes.SET_CURRENT_ACCOUNT_USER_GROUPS, newValue);
      }
    });

    Object.defineProperty(accountsServiceFactory, 'currentAccountIsPaused', {
      get: function () {
        return currentAccountIsPaused;
      },
      set: function (newValue) {
        currentAccountIsPaused = newValue;
        VuexStore.commit(VuexMutationTypes.SET_CURRENT_ACCOUNT_IS_PAUSED, newValue);
      }
    });

    return accountsServiceFactory;

    function isAffiliate() {
      return _hasOnlyRole('affiliate') || _hasOnlyRole('affiliate_manager');
    }

    function isBuyer() {
      return _hasOnlyRole('buyer') || _hasOnlyRole('buyer_manager');
    }

    function isAffiliateOnly() {
      return _hasOnlyRole('affiliate');
    }

    function isBuyerOnly() {
      return _hasOnlyRole('buyer');
    }

    function isAffiliateManager() {
      return _hasOnlyRole('affiliate_manager');
    }

    function isBuyerManager() {
      return _hasOnlyRole('buyer_manager');
    }

    function isAdmin() {
      return _hasRole('admin');
    }

    function isReportReadOnlyUser() {
      return _hasOnlyRole('user');
    }

    // Read-only superuser impersonation: admin-shaped READ visibility, no write access.
    function isReadOnlySuperUser() {
      return _hasOnlyRole('readonly');
    }

    function resetAccountData() {
      $log.getInstance('accountsService').info('Resetting account data');
      cacheService.remove('accountsService.accountData');
      cacheService.remove('accountsService.currentUserName');
      cacheService.remove('accountsService.getAccount');
      cacheService.remove('accountsService.currentAccounts');
      cacheService.remove('accountsService.currentPermissions');
      cacheService.remove('accountsService.defaultAccount');
      featureFlagsService.disableAllFeatureFlags();
      accountsServiceFactory.currentAccounts = null;
      accountsServiceFactory.currentUserName = '';
      accountsServiceFactory.currentAccountId = null;
      accountsServiceFactory.currentAccount = null;
      accountsServiceFactory.accountPermissions = null;
      accountsServiceFactory.currentAccountUser = null;
      accountsServiceFactory.currentPermissions = {};
      accountsServiceFactory.currentAccountIsPaused = false;
    }

    function selectAccount(accountId) {
      accountsServiceFactory.currentAccountId = accountId;
      cacheService.set('accountsService.accountData', {
        currentAccountId: accountId
      }, {
        persistence: true
      });
      // using this. in the below statement helps with testing
      // check accounts.service.spec for details
      return this.setCurrentAccount(accountId);
    }

    function getAccount(accountId) {
      const cachedValue = cacheService.get('accountsService.getAccount');
      if (cachedValue && cachedValue.data && cachedValue.data.account
        && cachedValue.data.account.id === accountId
        && cachedValue.data.accountUser != null) {
        return $q.when(cachedValue);
      }

      const url = serviceBase + 'ringbaaccounts/' + accountId + '?getUserInfo=true';
      const cacheKey = promiseAccountCacheKey + accountId;
      return promiseCacheService.getCallWithCachedPromise(cacheKey, url, 0,
        function (response) {
          cacheService.set('accountsService.getAccount', response, {
            persistence: true,
            expireAfterSeconds: 60 * 60 * 5 // five hours
          });
        });
    }

    function getAccountRoles() {
      const url = serviceBase + 'RingbaAccounts/AccountRoles';
      return $http.get(url);
    }

    // Set current account and current account permissions
    function setCurrentAccount(accountId) {
      if (!accountId) {
        const errorMessage = 'No account selected.';
        return $q.reject({ data: { message: errorMessage } });
      }
      return getAccount(accountId)
        .then(function (response) {
          VuexStore.commit(VuexMutationTypes.RESET);
          accountsServiceFactory.currentAccount = response.data.account;
          accountsServiceFactory.currentAccountUser = response.data.accountUser;
          accountsServiceFactory.currentAccountId = accountsServiceFactory.currentAccount.id;
          accountsServiceFactory.currentPermissions = response.data.permissions;
          accountsServiceFactory.accountPermissions = accountPermissions(response.data.permissions);
          accountsServiceFactory.currentAccountIsPaused = response.data.isPaused;
          return true;
        })
        .then(_getGroupsForManager);
    }

    // RUM and system flags identify the same user in the same account, so both follow every change.
    function _syncIdentity() {
      const accountId = currentAccountId || null;
      const userLogin = currentUserName || null;

      systemFlagsService.setContext({ accountId: accountId, userLogin: userLogin });

      const reporter = window.__ringbaErrorReporter;
      if (reporter && typeof reporter.setUser === 'function') {
        reporter.setUser(userLogin, accountId);
      }
    }

    function accountPermissions(permissions) {
      const itemAccess = [];
      const baseItemAccess = [{
        type: 'CallLog',
        permissions: ['READ'],
        fullAccess: false
      },
      {
        type: 'CallBack',
        permissions: ['READ', 'CREATE'],
        fullAccess: false
      }
      ];
      angular.forEach(permissions.itemAccess, function (item) {
        itemAccess.push({
          type: item.type,
          permissions: item.permissions.split(', '),
          fullAccess: item.fullAccess
        });
      });

      // Read-only superuser impersonation: grant READ/LIST on the item types whose backend reads
      // are enabled for the role (no CREATE/UPDATE/DELETE, and never 'Admin'/'ALL'), so read menu
      // items appear while write and admin-management screens stay hidden. Keep in step with the
      // backend READONLY read-enablement batches.
      // hasAccess() resolves via _.find on itemAccess.concat(baseItemAccess) — first match wins —
      // so merge into an existing server itemAccess entry instead of pushing a shadowed duplicate.
      const roles = permissions && permissions.roles;
      if (roles && roles.length === 1 && roles[0] === 'readonly') {
        ['Dashboard', 'Campaign', 'Offer', 'Affiliate', 'Target', 'TargetGroup', 'Buyer', 'Number', 'NumberPool', 'PingTree', 'Report'].forEach(function (type) {
          const existing = _.find(itemAccess, { type: type });
          if (existing) {
            existing.permissions = _.union(existing.permissions, ['READ', 'LIST']);
          } else {
            baseItemAccess.push({ type: type, permissions: ['READ', 'LIST'], fullAccess: false });
          }
        });
      }

      const returnPermissions = itemAccess.concat(baseItemAccess);
      return returnPermissions;
    }

    function hasAccess(itemType, permission) {
      let item;
      let access = false;

      if (isAdmin()) {
        access = true;
      } else {
        item = _.find(accountsServiceFactory.accountPermissions, {
          'type': itemType
        });

        if (typeof item !== 'undefined') {
          if (_.includes(item.permissions, permission)) {
            access = true;
          }
        }
      }

      return access;
    }

    function getAccountDefaultPermissionItem() {
      return hasAccess('Dashboard', 'READ') ? 'Dashboard' : 'CallLog';
    }

    function fetchAccountDataFromCache() {
      accountsServiceFactory.currentAccounts = cacheService.get('accountsService.currentAccounts');
      const getAccountResponse = cacheService.get('accountsService.getAccount');
      if (getAccountResponse && getAccountResponse.data) {
        accountsServiceFactory.currentAccount = getAccountResponse.data.account;
        accountsServiceFactory.currentAccountUser = getAccountResponse.data.accountUser;
        accountsServiceFactory.currentAccountId = accountsServiceFactory.currentAccount.id;
        accountsServiceFactory.currentPermissions = getAccountResponse.data.permissions;
        accountsServiceFactory.accountPermissions = accountPermissions(getAccountResponse.data.permissions);
        accountsServiceFactory.currentUserName = cacheService.get('accountsService.currentUserName');
        accountsServiceFactory.currentAccountIsPaused = getAccountResponse.data.isPaused;
      }
    }

    function _userChanged() {
      return accountsServiceFactory.currentUserName !== authService.authentication.userName;
    }

    async function loadAccountsData() {
      fetchAccountDataFromCache();
      if (!accountsServiceFactory.currentAccountId || _userChanged()) {
        return _resetAndPickAccount();
      }
      if (accountsServiceFactory.currentAccounts) {
        return _getGroupsForManager();
      }

      // The selected account stays set while the list refreshes, so no request goes out without an account id.
      return getAccounts().then(function (accounts) {
        if (!_.some(accounts, { id: accountsServiceFactory.currentAccountId })) {
          return _resetAndPickAccount();
        }
        return setCurrentAccount(accountsServiceFactory.currentAccountId);
      }).catch(_showError);
    }

    function _resetAndPickAccount() {
      resetAccountData();
      return getDefaultAccount().then(function () {
        return getAccounts()
          .then(function (accounts) {
            // IF the user doesn't have accounts then that means they have been removed from all his linked ringba accounts
            if (accounts.length === 0) {
              authService.logOut();
              return $q.reject({ data: { message: 'No accounts linked to your user' } });
            }
            accountsServiceFactory.currentUserName = authService.authentication.userName;
            cacheService.set('accountsService.currentUserName', accountsServiceFactory.currentUserName, {
              persistence: true
            });
            let defaultAccount = cacheService.get('accountsService.defaultAccount');
            if (!defaultAccount || !_.some(accounts, {id: defaultAccount.id})) {
              defaultAccount = accounts && accounts.length ? accounts[0] : null;
              saveDefaultAccount(defaultAccount);
            }
            return _getWhitelabelAccount(accounts);
          }).then(function (accountId) {
            const defaultAccount = cacheService.get('accountsService.defaultAccount');
            const defaultAccountId = defaultAccount ? defaultAccount.id : null;
            accountId = accountId || defaultAccountId;

            // If the account we got doesn't match with accounts of this user, kick him off
            if (!_.some(accountsServiceFactory.currentAccounts, { id: accountId })) {
              authService.logOut();
              return $q.reject({ data: { message: 'No accounts linked to your user' } });
            }

            return setCurrentAccount(accountId);
          })
          .catch(_showError);
      });
    }

    function _showError(error) {
      toaster.pop('error', error.data.message);
    }

    function _getGroupsForManager() {
      if (_hasOnlyRole('buyer_manager') || _hasOnlyRole('affiliate_manager')) {
        return _getGroupsForUser(accountsServiceFactory.currentAccountId);
      }
      return true;
    }

    function getAccounts() {
      const url = serviceBase + 'ringbaaccounts';
      return promiseCacheService.getCallWithCachedPromise(promiseAccountsCacheKey, url, 0,
        function (response) {
          cacheService.set('accountsService.currentAccounts', response.data.account,{
            persistence: true,
            expireAfterSeconds: 60 * 60 * 5 // five hour
          });
          accountsServiceFactory.currentAccounts = response.data.account;
        }).then(function (response) {
        return response.data.account;
      });
    }

    function updateAccount(account) {
      // Promisification done
      const url = serviceBase + 'ringbaAccounts/' + this.currentAccountId;
      return $http.patch(url, account)
        .then(function (response) {
          resetAccountData();
          return $q.all([response, loadAccountsData()]);
        })
        .then(function (results) {
          const response = results[0];
          return response;
        });
    }

    function createAccount(account) {
      // Promisification done
      accountsServiceFactory.currentUserName = '';
      accountsServiceFactory.currentAccounts = null;
      accountsServiceFactory.currentAccountId = null;
      accountsServiceFactory.currentAccountUser = null;
      const url = serviceBase + 'ringbaaccounts';
      return $http.post(url, account)
        .then(function (response) {
          resetAccountData();
          return $q.all([response, loadAccountsData()]);
        })
        .then(function (results) {
          const response = results[0];
          return response;
        });

    }

    function saveDefaultAccount(account) {
      cacheService.set('accountsService.defaultAccount', account, {
        persistence: true
      });
      userSettingsService.getUserSettings()
        .then(function (userSettings) {
          userSettings.defaultAccount = account;
          return userSettingsService.saveUserSettings(userSettings);
        });
    }

    function getDefaultAccount() {
      return userSettingsService.getUserSettings().then(function (userSettings) {
        if (userSettings.defaultAccount) {
          cacheService.set('accountsService.defaultAccount', userSettings.defaultAccount, {
            persistence: true
          });
        } else {
          cacheService.remove('accountsService.defaultAccount');
        }
      });
    }

    function _getGroupsForUser(accountId) {
      return userGroupService.getGroupsForUser(accountId)
        .then(function (response) {
          accountsServiceFactory.currentAccountUserGroups = response.data.managers;
          return true;
        }).catch(function () {
          return true;
        });
    }

    function _getWhitelabelAccount(accounts) {
      if (whitelabelService.checkLocationHostIsBase()) {
        let accountToReturn = null;
        if (accounts && accounts.length) {
          const defaultAccount = cacheService.get('accountsService.defaultAccount');
          const existsInAccounts = defaultAccount && !!accounts.find(function (account) {
            return account.id === defaultAccount.id;
          });
          accountToReturn = existsInAccounts ? defaultAccount.id : accounts[0].id;
        }
        return $q.when(accountToReturn);
      } else {
        return whitelabelService.getPublicWlSettings()
          .then(function (response) {
            return response.data && response.data.accountId;
          })
          .catch(function () {
            authService.logOut();
            return $q.reject({ data: { message: 'Failed to get WLS' } });
          });
      }
    }

    function _hasRole(role) {
      return _.includes(_getRoles(), role);
    }

    function _hasOnlyRole(role) {
      const roles = _getRoles();
      return (roles && roles.length === 1 && roles[0] === role);
    }

    function _getRoles() {
      if (accountsServiceFactory && accountsServiceFactory.currentPermissions && accountsServiceFactory.currentPermissions.roles) {
        return accountsServiceFactory.currentPermissions.roles;
      } else {
        return [];
      }

    }

  }
})();
